import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export function FloatingAuthModal() {
  const { user, login } = useAuth();
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if user is authenticated or if dismissed
  if (user || isDismissed) return null;

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setIsLoading(true);
    try {
      login(token);
      toast({
        title: 'Logged in successfully',
        description: 'Welcome to CADAM!',
      });
    } catch (error) {
      toast({
        title: 'Login failed',
        description:
          error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Sign in"
      className="fixed bottom-6 right-6 z-50 w-[340px] overflow-hidden rounded-xl border border-white/10 bg-[#191A1A]/95 shadow-2xl backdrop-blur-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
    >
      {/* Header with close button */}
      <div className="absolute right-4 top-4 z-10">
        <button
          onClick={() => setIsDismissed(true)}
          className="rounded-full p-1 text-adam-neutral-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 pt-10">
        <div className="flex flex-col gap-5">
          {/* Logo and Title */}
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-adam-blue/20 to-transparent">
              <img
                src="/adam-logo.svg"
                alt="Logo"
                className="h-6 w-6"
              />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold leading-tight text-white">
                Welcome to CADAM
              </h3>
              <p className="text-sm text-adam-neutral-400">
                Enter your family access token
              </p>
            </div>
          </div>

          {/* Token Input */}
          <form onSubmit={handleTokenSubmit} className="flex flex-col gap-3">
            <Input
              type="password"
              placeholder="Enter access token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="h-10 rounded-xl border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-adam-neutral-500 focus:border-adam-blue/50 focus:bg-white/10 focus:ring-0"
              disabled={isLoading}
            />
            <Button
              type="submit"
              className="w-full rounded-xl bg-adam-blue py-5 text-sm font-medium text-white shadow-[0_0_20px_-5px_rgba(0,166,255,0.4)] transition-all hover:bg-[#0095E6] hover:shadow-[0_0_25px_-5px_rgba(0,166,255,0.5)]"
              disabled={isLoading || !token}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
